import React from 'react';
import classNames from 'classnames';
import { Modal } from 'antd';
import dayjs from 'dayjs';
import UberHealthIcon from '../../../assets/svgs/uberHealth.svg';
import {
  getDetailedViewStatusColor,
  getDetailedViewStatusLabel,
  getDetailedViewVehicleTypeDisplay,
  getDetailedViewVehicleTypeSideInfo,
  getOrganizationName,
  getTransportCompanyName,
} from './detailedViewHelpers';
import styles from './InsightsRideDetailsModal.module.css';

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const date = dayjs(value);
  return date.isValid() ? date.format('MMM D, YYYY h:mm A') : 'N/A';
};

const getRiderName = (ride) => {
  const fromInfo = `${ride?.riderInfo?.firstName || ''} ${ride?.riderInfo?.lastName || ''}`.trim();
  if (fromInfo) return fromInfo;
  if (ride?.rider) return ride.rider;
  return 'N/A';
};

const getDriverName = (ride, drivers) => {
  if (ride?.driverId) {
    const driver = drivers.find((item) => item.id === ride.driverId);
    if (driver?.name) return driver.name;
  }
  if (ride?.driverName) return ride.driverName;
  return 'Not assigned';
};

const getCreatedByLabel = (ride) => {
  if (ride?.createdBy?.firstName || ride?.createdBy?.lastName) {
    return `${ride.createdBy.firstName || ''} ${ride.createdBy.lastName || ''}`.trim();
  }
  if (typeof ride?.createdBy === 'string' && ride.createdBy) {
    return ride.createdBy;
  }
  if (ride?.createdById) {
    return `User ${ride.createdById}`;
  }
  return 'N/A';
};

const TimelineRow = ({ label, value }) => (
  <div className={styles.timelineRow}>
    <span className={styles.timelineLabel}>{label}</span>
    <span
      className={classNames(styles.timelineValue, {
        [styles.timelineValueNa]: value === 'N/A',
      })}
    >
      {value}
    </span>
  </div>
);

const DetailField = ({ label, value }) => (
  <div className={styles.detailItem}>
    <div className={styles.detailLabel}>{label}</div>
    <div className={styles.detailValue}>{value}</div>
  </div>
);

const VEHICLE_TYPE_SIDE_INFO_CHARS_PER_LINE = 24;

const splitIntoCharacterLines = (text, charsPerLine = VEHICLE_TYPE_SIDE_INFO_CHARS_PER_LINE) => {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return [];

  const lines = [];
  let currentLine = '';

  words.forEach((word) => {
    const candidate = currentLine ? `${currentLine} ${word}` : word;

    if (candidate.length <= charsPerLine) {
      currentLine = candidate;
      return;
    }

    if (currentLine) {
      lines.push(currentLine);
    }

    currentLine = word.length > charsPerLine ? '' : word;
    if (word.length > charsPerLine) {
      lines.push(word);
    }
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
};

const VehicleTypeValue = ({ vehicleType }) => {
  const display = getDetailedViewVehicleTypeDisplay(vehicleType);
  const sideInfo = getDetailedViewVehicleTypeSideInfo(vehicleType);

  return (
    <div className={styles.vehicleTypeRow}>
      <span
        className={classNames(styles.vehicleTypePill, {
          [styles.vehicleTypePillStacked]: display.secondary,
        })}
      >
        {display.isUberHealth ? (
          <span className={styles.vehicleTypeUberRow}>
            <img src={UberHealthIcon} alt="" className={styles.uberHealthIcon} />
            <span className={styles.vehicleTypePrimary}>{display.primary}</span>
          </span>
        ) : (
          <>
            <span className={styles.vehicleTypePrimary}>{display.primary}</span>
            {display.secondary && (
              <span className={styles.vehicleTypeSecondary}>{display.secondary}</span>
            )}
          </>
        )}
      </span>
      {sideInfo && (
        <span className={styles.vehicleTypeDescription}>
          {splitIntoCharacterLines(sideInfo).map((line, index) => (
            <span key={index} className={styles.vehicleTypeDescriptionLine}>
              {line}
            </span>
          ))}
        </span>
      )}
    </div>
  );
};

export const InsightsRideDetailsModal = ({
  open,
  ride,
  onClose,
  healthOrganizations = [],
  transportCompanies = [],
  drivers = [],
  hideTransportCompanyAndDriver = false,
}) => {
  if (!ride) {
    return (
      <Modal
        className={styles.modal}
        open={open}
        onCancel={onClose}
        footer={null}
        width={960}
        destroyOnClose
      />
    );
  }

  const statusColor = getDetailedViewStatusColor(ride.status);
  const statusLabel = getDetailedViewStatusLabel(ride.status);
  const pickupAddress = ride.pickupAddressDetails?.formattedAddress || 'N/A';
  const destinationAddress = ride.destinationAddressDetails?.formattedAddress || 'N/A';
  const waitTimeLabel =
    ride.waitTime != null && ride.waitTime !== ''
      ? `${ride.waitTime} minute${Number(ride.waitTime) === 1 ? '' : 's'}`
      : 'N/A';

  return (
    <Modal
      className={styles.modal}
      open={open}
      onCancel={onClose}
      footer={null}
      width={960}
      destroyOnClose
      title={null}
    >
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerEyebrow}>Ride Details</div>
          <div className={styles.rideIdRow}>
            <span className={styles.rideIdLabel}>Ride ID</span>
            <span className={styles.rideIdValue}>{ride.seqId || 'N/A'}</span>
          </div>
        </div>
        <div className={styles.headerRight}>
          <div className={styles.statusEyebrow}>Current Status</div>
          <span
            className={styles.statusPill}
            style={{
              color: statusColor,
              borderColor: statusColor,
            }}
          >
            {statusLabel}
          </span>
        </div>
      </div>

      <div className={styles.body}>
        <div className={styles.leftColumn}>
          <section className={styles.additionalInfoCard}>
            <h3 className={styles.additionalInfoTitle}>Additional Info</h3>
            <div className={styles.routeItinerary}>
              <div className={styles.routeStop}>
                <div className={styles.routeStopHeader}>
                  <span className={styles.routeDot} aria-hidden />
                  <span className={styles.locationLabel}>Pickup Location</span>
                </div>
                <div className={styles.routeStopBody}>
                  <div className={styles.routeLineColumn}>
                    <span className={styles.routeLineConnector} aria-hidden />
                  </div>
                  <div className={styles.routeStopContent}>
                    <div className={styles.locationValue}>{pickupAddress}</div>
                    <div className={styles.pickupDateRow}>
                      <span className={styles.pickupDateLabel}>Pickup Date: </span>
                      <span className={styles.pickupDateValue}>
                        {formatDateTime(ride.pickUpDate)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.routeStop}>
                <div className={styles.routeStopHeaderMileage}>
                  <span className={styles.routeDot} aria-hidden />
                  <span className={styles.destinationLabel}>Destination</span>
                  <span className={styles.mileageLabel}>MILEAGE</span>
                </div>
                <div className={styles.routeStopBodyDestination}>
                  <div className={styles.routeLineColumn}>
                    <span className={styles.routeLineTail} aria-hidden />
                  </div>
                  <div className={styles.destinationValue}>{destinationAddress}</div>
                  <div className={styles.mileageValue}>
                    {ride.actMileage ? `${ride.actMileage} miles` : 'N/A'}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className={styles.timelineCard}>
            <h3 className={styles.sectionTitle}>Ride Timeline</h3>
            <div className={styles.timelineList}>
              <TimelineRow label="Created At" value={formatDateTime(ride.createdAt)} />
              <TimelineRow label="Accepted At" value={formatDateTime(ride.acceptanceTime)} />
              <TimelineRow label="Actual Pickup" value={formatDateTime(ride.actualPickUpDate)} />
              <TimelineRow label="Completed At" value={formatDateTime(ride.unloadTime)} />
              <TimelineRow label="Wait Time" value={waitTimeLabel} />
            </div>
          </section>
        </div>

        <div className={styles.rightColumn}>
          <section className={styles.detailsCard}>
            <h3 className={styles.rideDetailsTitle}>Ride Details</h3>
            <div className={styles.detailsList}>
              {!hideTransportCompanyAndDriver && (
                <>
                  <DetailField
                    label="TRANSPORT COMPANY"
                    value={getTransportCompanyName(ride, transportCompanies)}
                  />
                  <DetailField label="DRIVER" value={getDriverName(ride, drivers)} />
                </>
              )}
              <DetailField
                label="HEALTH ORGANIZATION"
                value={getOrganizationName(ride, healthOrganizations)}
              />
              <DetailField label="RIDER" value={getRiderName(ride)} />
              <DetailField label="CREATED BY" value={getCreatedByLabel(ride)} />
              <div className={styles.detailItem}>
                <div className={styles.detailLabel}>VEHICLE TYPE</div>
                <VehicleTypeValue vehicleType={ride.vehicleType} />
              </div>
              <div className={styles.detailRow}>
                <DetailField
                  label="APPOINTMENT DATE"
                  value={formatDateTime(ride.appointmentDate)}
                />
                <DetailField
                  label="IS RECURRING"
                  value={ride.isRecurring ? 'Yes' : 'No'}
                />
              </div>
            </div>
          </section>
        </div>
      </div>
    </Modal>
  );
};
